import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getChildId } from "@/lib/childId";
import { recordSkillEvent } from "@/lib/skillLedger";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MicroCheck — ONE quick question from the existing bank for the lesson's objective (no new content system).
 * Wrong ⇒ TEACH (authored explanation + error pattern) and a "try one more" from the same objective — never just "wrong".
 * Results go to the same bank history and the device skill ledger (kind "micro-check"); a single item is deliberately
 * NOT written as school CHECK evidence — the proper check/practice sessions remain the mastery-bearing path.
 */
export default function MicroCheck({ code, tier = "explorer", subject = "" }) {
  const [q, setQ] = useState(undefined);
  const [chosen, setChosen] = useState(null);
  const [fb, setFb] = useState(null);
  const [tries, setTries] = useState(0);
  const childId = getChildId();
  const load = (responses, exclude) =>
    fetch(`${API}/questions/adaptive-next`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ child_id: childId, outcome_codes: [code], age_tier: tier, responses, exclude_ids: exclude }) })
      .then((r) => (r.ok ? r.json() : { question: null })).then((j) => setQ(j.question || null)).catch(() => setQ(null));
  useEffect(() => { if (code) load([{ question_id: "warm", was_correct: true, difficulty: 2 }], []); }, [code, tier]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!code || q === null || q === undefined) return null;
  const correct = chosen !== null && chosen === q.correct_answer;
  const answer = (opt) => {
    if (chosen !== null) return;
    setChosen(opt);
    const ok = opt === q.correct_answer;
    recordSkillEvent({ skillId: `objective:${code}`, correct: ok, kind: "micro-check", skillType: subject || null });
    track("micro_check_answered", { code, correct: ok, try: tries + 1 });
    fetch(`${API}/questions/answer`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ child_id: childId, question_id: q.id, was_correct: ok, chosen: String(opt) }) })
      .then((r) => (r.ok ? r.json() : null)).then((j) => setFb(j?.feedback || null)).catch(() => {});
  };
  const tryAnother = () => { setTries((t) => t + 1); setChosen(null); setFb(null); setQ(undefined); load([{ question_id: q.id, was_correct: false, difficulty: Number(q.difficulty) || 2 }], [q.id]); };
  return (
    <section className="mt-6 rounded-3xl border-[3px] border-slate-900 bg-sky-50 p-4" data-testid="lesson-micro-check" aria-labelledby="micro-check-title">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">Quick check</p>
      <h2 id="micro-check-title" className="mt-1 font-heading text-lg font-bold leading-snug">{q.question_text}</h2>
      <ul className="mt-3 grid gap-2">
        {(q.options || []).map((opt, i) => (
          <li key={i}>
            <button type="button" onClick={() => answer(opt)} disabled={chosen !== null} aria-pressed={chosen === opt} data-testid={`micro-check-option-${i}`}
              className={`w-full text-left min-h-[48px] rounded-2xl border-[3px] px-4 py-2 font-bold text-sm ${chosen === null ? "border-slate-900 bg-white hover:bg-yellow-50" : opt === q.correct_answer ? "border-emerald-800 bg-emerald-100" : chosen === opt ? "border-rose-700 bg-rose-50" : "border-slate-300 bg-white text-slate-500"}`}>
              {opt}
            </button>
          </li>
        ))}
      </ul>
      {chosen !== null && (
        <div className="mt-3 rounded-2xl border-2 border-slate-900 bg-white p-3" data-testid="micro-check-feedback">
          <p className="font-extrabold text-sm">{correct ? "✓ Yes — you've got this." : "Not quite — let's look at it together."}</p>
          {!correct && fb?.note && <p className="mt-1 text-xs font-bold text-amber-900" data-testid="micro-check-pattern">💡 {fb.note}</p>}
          {q.explanation && <p className="mt-1 text-xs md:text-sm font-bold text-slate-700">{q.explanation}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            {!correct && tries < 2 && <button type="button" onClick={tryAnother} data-testid="micro-check-try-another" className="min-h-[40px] px-3 rounded-full border-2 border-slate-900 bg-yellow-300 text-xs font-bold">Try one more like this</button>}
            <Link to={`/practice/${code}`} data-testid="micro-check-practice" className="min-h-[40px] inline-flex items-center px-3 rounded-full border-2 border-slate-900 bg-white text-xs font-bold">{correct ? "Practise to make it stick" : "Practise this step by step"}</Link>
          </div>
        </div>
      )}
    </section>
  );
}
